import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, AlertTriangle, Mail, Loader2, ExternalLink, TrendingUp, CalendarClock } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MEDALS = ['🥇', '🥈', '🥉'];

export const WeeklyRankingCard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reports/weekly-ranking`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/reports/weekly-ranking/send`);
      const st = res.data.email?.status;
      if (st === 'sent') toast.success(`Ranking enviado a ${res.data.email.email}`);
      else if (st === 'skipped') toast.info(`Guardado en la app · email omitido (${res.data.email.reason})`);
      else toast.error(res.data.email?.error || 'No se pudo enviar el email');
      setData({ ...res.data.ranking, last_sent: { sent_at: new Date().toISOString() } });
    } catch {
      toast.error('Error al enviar el ranking');
    } finally {
      setSending(false);
    }
  };

  if (loading) return null;
  if (!data) return null;

  const best = data.best_campaign;
  const obj = data.top_objection;

  return (
    <Card className="border-border/60 bg-brand-copper/5"
      data-testid="weekly-ranking-card">
      <CardContent className="p-4 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-700 dark:text-amber-400" />
          <p className="text-sm font-semibold">Ranking semanal</p>
          <Badge variant="outline" className="text-[10px] gap-1">
            <CalendarClock className="w-3 h-3" />
            {data.week_start?.slice(0, 10)} → {data.week_end?.slice(0, 10)}
          </Badge>
          <Button size="sm" variant="outline" className="ml-auto gap-1" onClick={sendNow} disabled={sending}
            data-testid="weekly-ranking-send">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            Enviármelo por email
          </Button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <button onClick={() => navigate('/deals')} data-testid="weekly-ranking-best"
            className="text-left p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/25 hover:bg-emerald-500/15 transition-colors">
            <p className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5" /> Cerró mejor esta semana
            </p>
            <p className="mt-1 text-base font-semibold truncate">{best?.name || 'Sin campañas todavía'}</p>
            <p className="text-[11px] text-muted-foreground">
              {best ? `${best.conversion_rate}% de cierre · ${best.won} ventas · $${Number(best.won_value).toLocaleString()}` : '—'}
            </p>
          </button>

          <button onClick={() => navigate('/deals')} data-testid="weekly-ranking-objection"
            className="text-left p-3 rounded-xl bg-amber-500/10 border border-amber-500/25 hover:bg-amber-500/15 transition-colors">
            <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> Objeción que más frenó ventas
            </p>
            <p className="mt-1 text-base font-semibold capitalize truncate">{obj?.text || 'Ninguna detectada'}</p>
            <p className="text-[11px] text-muted-foreground truncate">
              {obj ? `${obj.count} menciones · ${[...new Set(obj.campaigns || [])].join(', ')}` : '—'}
            </p>
          </button>
        </div>

        {data.campaigns?.length > 0 && (
          <div className="space-y-1.5" data-testid="weekly-ranking-list">
            {data.campaigns.slice(0, 5).map((r, i) => (
              <div key={r.id} className="flex items-center gap-2 p-2 rounded-lg bg-muted/20 text-xs">
                <span className="w-5 text-center">{MEDALS[i] || `${i + 1}.`}</span>
                <span className="flex-1 truncate font-medium">{r.name}</span>
                <span className={cn('tabular-nums', i === 0 ? 'text-emerald-700 dark:text-emerald-400 font-semibold' : 'text-muted-foreground')}>
                  {r.conversion_rate}%
                </span>
                <span className="tabular-nums text-muted-foreground w-20 text-right">
                  ${Number(r.won_value).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}

        <p className="text-[11px] text-muted-foreground flex flex-wrap items-center gap-1">
          Se envía automáticamente cada lunes a tu correo.
          {data.last_sent?.sent_at && <> Último envío: {new Date(data.last_sent.sent_at).toLocaleString('es')}.</>}
          <button onClick={() => navigate('/analytics')} className="text-brand-deep dark:text-brand-deep dark:text-brand-green hover:underline inline-flex items-center gap-1"
            data-testid="weekly-ranking-email-config">
            Cambiar correo <ExternalLink className="w-3 h-3" />
          </button>
        </p>
      </CardContent>
    </Card>
  );
};
